import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import '@v2/styles/tailwind.css';
import { AccountsTable, AccountDetails } from './components';
import { Account } from '@v2/types';
import { useAccounts, useAccountDetails, useTableFilters } from '@v2/hooks';
import { AccountForm } from '@v2/features';
import { Button, TableFilters } from '@v2/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUsers } from '@fortawesome/free-solid-svg-icons';

const sortOptions = [
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'email', label: 'Email'}
];

const AccountsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const selectedAccountId = searchParams.get('accountId') || null;

  const {
    accounts,
    loading: accountsLoading,
    error: accountsError,
    refetch: refetchAccounts,
  } = useAccounts();

  const {
    filteredData,
    search,
    setSearch,
    sortConfig,
    handleSort,
    toggleSortDirection,
    clearSort,
  } = useTableFilters({
    data: accounts,
    searchKeys: ['firstName', 'lastName', 'email'],
  });

  const {
    account,
    loading: detailsLoading,
    error: detailsError,
    refetch: refetchDetails,
  } = useAccountDetails(selectedAccountId);

  const handleSelectAccount = (selectedAccount: Account) => {
    searchParams.set('accountId', selectedAccount.id);
    history.push({ search: searchParams.toString() });
  };

  const handleClosePanel = () => {
    searchParams.delete('accountId');
    history.push({ search: searchParams.toString() });
  };

  const handleCreateAccount = () => {
    setIsCreating(true);
    setIsFormOpen(true);
  };

  const handleEditAccount = () => {
    setIsCreating(false);
    setIsFormOpen(true);
  };

  const handleCancelForm = () => {
    setIsFormOpen(false);
    setIsCreating(false);
  };

  const handleFormCompleted = (newAccountId?: string) => {
    setIsFormOpen(false);
    setIsCreating(false);
    
    refetchAccounts();
    
    if (newAccountId) {
      searchParams.set('accountId', newAccountId);
      history.push({ search: searchParams.toString() });
    } else {
      refetchDetails();
    }
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-p-6" style={{ height: 'calc(100vh - 65px)' }}>
      <div className="tw-flex tw-items-center tw-justify-between">
        <h1 className="tw-text-3xl tw-font-bold tw-text-white-800 tw-flex tw-items-center tw-gap-4 tw-m-0 tw-leading-none">
          <FontAwesomeIcon icon={faUsers} />
          <span>Accounts</span>
        </h1>
        <Button
          variant="primary"
          onClick={handleCreateAccount}
          icon={<FontAwesomeIcon icon={faPlus} />}
        >
          Create Account
        </Button>
      </div>
      <div className="tw-w-full tw-mt-4">
        <TableFilters<Account>
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder="Search by name or email..."
              sortOptions={sortOptions}
              sortConfig={sortConfig}
              onSortChange={handleSort}
              onToggleSortDirection={toggleSortDirection}
              onClearSort={clearSort}
            />
        </div>
      <div className="tw-flex-1 tw-flex tw-gap-4 tw-p-2 tw-overflow-hidden">
        <div
          className={`tw-flex tw-flex-col tw-transition-all tw-duration-300 tw-ease-in-out ${
            selectedAccountId ? 'tw-w-1/2' : 'tw-w-full'
          }`}
        >
          <AccountsTable
            accounts={filteredData}
            loading={accountsLoading}
            error={accountsError}
            onSelectAccount={handleSelectAccount}
            selectedAccountId={selectedAccountId || undefined}
          />
        </div>

        {selectedAccountId && (
          <AccountDetails
            account={account}
            loading={detailsLoading}
            error={detailsError}
            onRetry={refetchDetails}
            onClose={handleClosePanel}
            onEdit={handleEditAccount}
          />
        )}
      </div>

      {isFormOpen && (
        <AccountForm
          account={isCreating ? null : account}
          onCancel={handleCancelForm}
          onCompleted={handleFormCompleted}
        />
      )}
    </div>
  );
};

export default AccountsPage;
